import { Link } from "react-router-dom";
import { Lock, ShieldCheck, EyeOff, ServerCog, BadgeAlert, ArrowRight } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHNlY3VyaXR5JTIwZGFya3xlbnwwfHx8fDE3ODY2NDk2MjV8MA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: Lock, title: "Encryption in Transit", body: "All traffic to and from LinkExpln is protected with HTTPS/TLS. Submitted URLs travel encrypted, end to end." },
  { icon: EyeOff, title: "Minimal Data Collection", body: "We collect only what is needed to analyze the links you submit. No browsing surveillance, no data resale, ever." },
  { icon: ServerCog, title: "Hardened Infrastructure", body: "Our services run on managed, isolated infrastructure with least-privilege access controls and continuous monitoring." },
  { icon: ShieldCheck, title: "No Secrets in the Browser", body: "The website ships zero API keys, credentials or private configuration to your browser. Sensitive logic stays server-side." },
];

export default function Security() {
  return (
    <>
      <Seo
        title="Security — LinkExpln"
        description="How LinkExpln protects your data: HTTPS everywhere, minimal data collection, hardened infrastructure and responsible disclosure for security researchers."
      />

      <section className="relative pt-24 pb-20 sm:pt-32 sm:pb-28 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-[#030711]/80 to-[#030711]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">Our Posture</p>
          <h1
            data-testid="security-heading"
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            <MaskedLines lines={["SECURITY IS", "THE PRODUCT."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-300 text-base sm:text-lg leading-relaxed">
            A security product must hold itself to a higher standard.
            Here is how LinkExpln handles your data and protects its own perimeter.
          </p>
        </div>
      </section>

      <section data-testid="security-pillars" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.title} delay={(i % 2) * 0.08} className="bg-[#0A121D] p-8 sm:p-12 hover:bg-[#101A28] transition-colors duration-300">
                <p.icon size={26} className="text-[#00C5FE] mb-5" />
                <h2 className="font-heading font-bold text-xl text-white">{p.title}</h2>
                <p className="mt-3 text-zinc-400 leading-relaxed">{p.body}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <FadeUp className="md:col-span-5">
            <BadgeAlert size={28} className="text-[#00C5FE] mb-5" />
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-white">
              Responsible disclosure
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <p className="text-zinc-400 leading-relaxed">
              Security researchers are part of the shield. If you believe you&apos;ve found
              a vulnerability in LinkExpln, we want to hear from you — before anyone
              else does. Reach out through our contact page and we&apos;ll respond promptly.
              We ask that you give us reasonable time to remediate before public disclosure.
            </p>
            <Link
              to="/contact"
              data-testid="security-contact-link"
              className="mt-6 inline-flex items-center gap-2 text-[#00C5FE] hover:text-white font-heading font-semibold transition-colors duration-200"
            >
              Contact the team <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
